import axios from 'axios';
import { debounce } from "debounce";
import { decode } from 'html-entities';
import { computed, inject, nextTick, reactive, ref, watch } from 'vue';
import { propsFactory } from '../util';
import { setHistoryState, getHistoryStateFromUrl } from './historyState';
import { get as getObjectByPath, set as setObjectByPath, merge as mergeObjects, isEqual } from 'lodash-es';
// Composables
export const makeAjaxListProps = propsFactory({
    getItemsUrl: {
        type: String,
        required: true,
    },
    getItemsContentUrl: {
        type: String,
        default: '',
    },
    getSolrResultsUrl: {
        type: String,
        default: '',
    },
    paginationType: {
        type: String,
        default: 'load-more-btn',
        validator: (value) => ['load-more-btn', 'pagination'].includes(value),
    },
    initialSearchQuery: {
        type: String,
        default: '',
    },
    itemsPerPage: {
        type: Number,
        default: 1,
    },
    type: {
        type: String,
        default: 'fetch',
        validator: (value) => ['fetch', 'search'].includes(value),
    },
});
class AjaxListFilter {
    constructor(data, props = {}) {
        this.initialData = JSON.parse(JSON.stringify(data));
        this.data = reactive(data);
    }
    resetFilter(property) {
        if (property) {
            setObjectByPath(this.data, property, JSON.parse(JSON.stringify(getObjectByPath(this.initialData, property))));
        }
        else {
            Object.assign(this.data, JSON.parse(JSON.stringify(this.initialData)));
        }
    }
    isPropertyEqualToInitialState(property) {
        return isEqual(getObjectByPath(this.initialData, property), getObjectByPath(this.data, property));
    }
}
class AjaxListPagination {
    constructor(options) {
        var _a, _b, _c;
        this.currentPage = ref(1);
        this.end = ref(0);
        this.itemsPerPage = ref((_a = options === null || options === void 0 ? void 0 : options.itemsPerPage) !== null && _a !== void 0 ? _a : 1);
        this.onSetIndexRange = (_b = options === null || options === void 0 ? void 0 : options.onSetIndexRange) !== null && _b !== void 0 ? _b : (() => { });
        this.start = ref(0);
        this.totalItems = ref(0);
        this.type = ref((_c = options === null || options === void 0 ? void 0 : options.type) !== null && _c !== void 0 ? _c : 'load-more-btn');
        watch(this.currentPage, () => {
            this.setIndexRange();
        });
    }
    get isLastPage() {
        return computed(() => this.end.value >= this.totalItems.value);
    }
    isOnCurrentPage(index) {
        return index >= this.start.value && index < this.end.value;
    }
    nextPage() {
        return ++this.currentPage.value;
    }
    reset() {
        this.currentPage.value === 1;
    }
    setIndexRange() {
        switch (this.type.value) {
            case 'load-more-btn':
                this.end.value = Math.min(this.currentPage.value * this.itemsPerPage.value, this.totalItems.value);
                break;
            case 'pagination':
                const maxPages = Math.max(Math.ceil(this.totalItems.value / this.itemsPerPage.value), 1);
                this.currentPage.value = Math.min(this.currentPage.value, maxPages);
                this.start.value = Math.min((this.currentPage.value - 1) * this.itemsPerPage.value, this.totalItems.value);
                this.end.value = Math.min(this.start.value + this.itemsPerPage.value, this.totalItems.value);
                break;
        }
        // Hook onSetIndexRange
        this.onSetIndexRange();
    }
}
class AjaxListSearch {
    constructor() {
        this.QUERY_LENGTH_THRESHOLD = 3;
        this.lastQuery = '';
        this.isLoading = ref(false);
        this.query = ref('');
        this.results = ref([]);
        this.fetchResultsDebounced = debounce(this.fetchResults, 300);
    }
    fetchResults(form) {
        if (!form || !form.action)
            return;
        if (this.lastQuery === this.query.value)
            return;
        if (this.query.value.length >= this.QUERY_LENGTH_THRESHOLD) {
            this.isLoading.value = true;
            let formData = new FormData(form);
            let solrQuery = this.query.value;
            let searchOriginal = axios.post(form.action, formData);
            solrQuery = solrQuery.split(" ").map(item => "*" + item + "*").join(" ");
            formData.set('tx_solr[q]', solrQuery);
            let searchWildcards = axios.post(form.action, formData);
            Promise.all([searchOriginal, searchWildcards])
                .then((responses) => {
                const results = [];
                responses.forEach(response => results.push(...response.data.searchResults));
                this.results.value = results;
            })
                .catch((error) => {
                console.error("Oops an error occurred: ", error);
            })
                .then(() => {
                this.isLoading.value = false;
            });
        }
        else {
            if (this.lastQuery.length >= this.QUERY_LENGTH_THRESHOLD) {
                this.results.value = [];
            }
        }
        this.lastQuery = this.query.value;
    }
    filterItems(items) {
        if (this.query.value.length < this.QUERY_LENGTH_THRESHOLD) {
            return items;
        }
        return items.filter(item => this.results.value.find(resultItem => item.uid === resultItem.uid));
    }
}
export class AjaxListItem {
    constructor() {
        this.content = '';
        this.uid = 0;
    }
}
export class AjaxList {
    constructor(props, filterData) {
        this.filter = new AjaxListFilter(filterData, props);
        this.filteredItems = ref([]);
        this.items = ref([]);
        this.isFetchingItems = ref(false);
        this.isLoading = ref(false);
        this.lazyLoad = inject('lazyLoad');
        this.pagination = new AjaxListPagination({
            itemsPerPage: props.itemsPerPage,
            onSetIndexRange: () => {
                if (!this.isFetchingItems.value) {
                    this.fetchItemsContent();
                }
            },
            type: props.paginationType
        });
        this.props = props;
        this.search = new AjaxListSearch();
        if (props.urlEncoding) {
            mergeObjects(this.filter.data, getHistoryStateFromUrl());
        }
        watch(this.filteredItems, (newItems) => {
            this.pagination.totalItems.value = newItems.length;
            this.pagination.setIndexRange();
        });
        watch([this.filter.data, this.search.results], () => {
            this.filterItems();
            this.pagination.reset();
            this.setHistoryState();
        }, { deep: true });
        watch(this.search.isLoading, () => {
            this.isLoading.value = this.search.isLoading.value;
        });
        this.fetchItems();
    }
    get visibleItems() {
        return computed(() => {
            return this.filteredItems.value.filter((item, index) => {
                return item.content && this.pagination.isOnCurrentPage(index);
            });
        });
    }
    addItems(items) {
        if (items.length) {
            items.reverse().forEach((item) => {
                this.items.value.unshift(item);
            });
        }
    }
    fetchItems() {
        this.isFetchingItems.value = true;
        this.isLoading.value = true;
        this.items.value = [];
        return axios({
            url: this.props.getItemsUrl,
            method: this.props.type === 'search' ? 'post' : 'get',
            // data: this.props.type === 'search' ? { query: this.filter.searchQuery } : null,
        })
            .then((response) => {
            this.isFetchingItems.value = false;
            this.addItems(response.data);
            this.setFilterOptions();
            this.filterItems();
            // this.initPage();
        })
            .catch((error) => {
            console.error('Oops an error occurred: ', error);
        });
    }
    fetchItemsContent() {
        if (this.props.getItemsContentUrl) {
            let params = this.fetchItemsContentPrepareParams();
            if (params.uids.length) {
                this.isLoading.value = true;
                axios.get(this.props.getItemsContentUrl, {
                    params
                })
                    .then((response) => {
                    response.data.forEach((itemWithContent) => {
                        const index = this.items.value.findIndex((item) => itemWithContent.uid === item.uid);
                        this.items.value[index].content = decode(itemWithContent.content);
                    });
                })
                    .catch((error) => {
                    console.error('Oops an error occurred: ', error);
                })
                    .then(() => {
                    this.handleItemsLoaded();
                });
            }
            else {
                this.handleItemsLoaded();
            }
        }
        else {
            this.handleItemsLoaded();
        }
    }
    fetchItemsContentPrepareParams() {
        const uids = [];
        for (let i = this.pagination.start.value; i < this.pagination.end.value; i++) {
            if (!this.filteredItems.value[i].content) {
                uids.push(this.filteredItems.value[i].uid);
            }
        }
        return {
            uids: uids.join(','),
        };
    }
    /**
     * This method is meant to be extended in specific component
     * to set up a filter logic for items
     */
    filterItems() {
        this.filteredItems.value = this.search.filterItems(this.items.value);
    }
    handleItemsLoaded() {
        this.isLoading.value = false;
        // this.updateState();
        nextTick(() => {
            if (this.lazyLoad) {
                this.lazyLoad.update();
            }
        });
    }
    /**
     * This method is meant to be extended in specific component
     * to set up filter options before "filterItems" method is triggered
     */
    setFilterOptions() { }
    setHistoryState() {
        if (this.props.id) {
            setHistoryState(this.props.id, this.filter.data, this.props.urlEncoding);
        }
    }
}
